<template>
  <v-layout wrap class="rich-text-container" @focus="focusEditor">
    <div  v-if="!readonly && fieldLabel" class="caption pb-2 opacity-54">{{fieldLabel}}</div>
    <div v-if="!readonly" style="width: 100%;">
      <wysiwyg v-if="!sourceMode" v-model="htmlContent" ref="wysiwygEditor" placeholder=""/>
      <v-textarea
        v-if="sourceMode"
        v-model="sourceHtml"
        @input="updateHtmlFromSource"
        box
        auto-grow
        rows="10"
        class="source-html-editor"
        placeholder="Edit HTML source code here...">
      </v-textarea>
    </div>
    <v-textarea
      v-if="readonly" :disabled="readonly" v-on:blur="blurred()"
      box :label="fieldLabel" v-model="htmlContent">
    </v-textarea>
  </v-layout>
</template>

<script>
import Helpers from '@/helpers.js'

export default {
  name: "rich-text",
  props: {
    field: {
      type: Object
    },
    data: {
      type: String
    },
    embedded: {
      type: Boolean,
      default: false
    },
    row: {
      type: Number
    },
    tabindex: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      sourceMode: false,
      sourceHtml: this.data || ''
    }
  },
  mounted() {
    // Initialize sourceHtml with current content
    this.sourceHtml = this.htmlContent || ''
    // Add source toggle button to wysiwyg toolbar
    this.$nextTick(() => {
      this.addSourceButtonToToolbar()
      this.setTabindexOnContentEditable()
    })
  },
  updated() {
    // Re-add button if wysiwyg editor is rendered
    if (!this.readonly && !this.sourceMode) {
      this.$nextTick(() => {
        this.addSourceButtonToToolbar()
        this.setTabindexOnContentEditable()
      })
    }
  },
  methods: {
    blurred() {
      this.$emit("blurred", {
        field: this.field.name,
        data: this.data,
        row: this.row
      })
    },
    focusEditor() {
      // Only forward focus if wysiwyg is rendered (not readonly mode)
      if (this.readonly || this.sourceMode) {
        return
      }
      
      // Use nextTick to ensure wysiwyg DOM is ready
      this.$nextTick(() => {
        // Find the editable content div inside wysiwyg
        const editableDiv = this.$el.querySelector('.editr--content')
        if (editableDiv) {
          editableDiv.focus()
        }
      })
    },
    toggleSourceMode() {
      if (!this.sourceMode) {
        // Switching to source mode - get current HTML content from the computed property
        this.sourceHtml = this.htmlContent || ''
      } else {
        // Switching back to visual mode - update HTML content via the computed setter
        this.htmlContent = this.sourceHtml
        // Re-add button to toolbar when switching back to visual mode
        this.$nextTick(() => {
          this.addSourceButtonToToolbar()
          this.setTabindexOnContentEditable()
        })
      }
      this.sourceMode = !this.sourceMode
    },
    updateHtmlFromSource() {
      // Update the HTML content when source is edited
      if (this.sourceMode) {
        if (this.field) {
          this.$emit('update-item', { field: this.field.name, data: this.sourceHtml, row: this.row })
        } else {
          this.$emit('update-item', { data: this.sourceHtml, row: this.row })
        }
      }
    },
    addSourceButtonToToolbar() {
      if (this.sourceMode || this.readonly) return // Don't add button in source mode or readonly
      
      const editorContainer = this.$el.querySelector('.rich-text-container')
      if (!editorContainer) return
      
      const toolbar = editorContainer.querySelector('.editr--toolbar')
      if (!toolbar) return
      
      // Check if button already exists
      if (toolbar.querySelector('.source-toggle-toolbar-btn')) return
      
      // Create the button
      const button = document.createElement('a')
      button.className = 'source-toggle-toolbar-btn'
      button.title = 'Source'
      button.innerHTML = '<i class="material-icons" style="font-size: 16px; line-height: 32px;">code</i>'
      button.style.cssText = 'display: inline-block; width: 30px; color: #333; cursor: pointer; text-align: center; line-height: 1;'
      button.addEventListener('click', (e) => {
        e.preventDefault()
        this.toggleSourceMode()
      })
      
      // Add separator if there are other buttons
      if (toolbar.children.length > 0) {
        const separator = document.createElement('a')
        separator.className = 'vw-btn-separator'
        separator.innerHTML = '<i class="vw-separator"></i>'
        toolbar.appendChild(separator)
      }
      
      // Add button to toolbar (at the end)
      toolbar.appendChild(button)
    },
    setTabindexOnContentEditable() {
      if (this.readonly || this.sourceMode) return // Don't set tabindex in source mode or readonly
      
      const editableDiv = this.$el.querySelector('.editr--content')
      if (editableDiv && this.tabindex !== null && this.tabindex !== undefined) {
        editableDiv.setAttribute('tabindex', this.tabindex)
      }
    }
  },
  watch: {
    data(newVal) {
      // Sync source HTML when the data prop changes (from parent)
      // Only sync when not in source mode to avoid overwriting user's source edits
      if (!this.sourceMode) {
        this.sourceHtml = newVal || ''
      }
    },
    tabindex() {
      // Update tabindex when prop changes
      if (!this.readonly && !this.sourceMode) {
        this.$nextTick(() => {
          this.setTabindexOnContentEditable()
        })
      }
    }
  },
  computed: {
    fieldLabel() {
      if (this.field) {
        if (this.field.display_name) {
          return this.field.display_name
        } else {
          return Helpers.capitalizeRemoveUnderscores(this.field.name)
        }
      } else {
        return undefined
      }
    },
    htmlContent: {
      get() {
        if (this.data) {
          return this.data
        } else {
          if (this.field && this.field.default_value) {
            return this.field.default_value
          } else {
            return ""
          }
        }
      },
      set(html) {
        if (this.field) {
          this.$emit('update-item', { field: this.field.name, data: html, row: this.row })
        } else {
          this.$emit('update-item', { data: html, row: this.row })
        }
      }
    },
    readonly() {
      let readonly = false
      if (this.field && this.field.readonly) { readonly = this.field.readonly }
      return readonly
    }
  }
}
</script>

<style>

.editr {
  border: 1px solid #e4e4e4;
  width: 100%;
}

.editr  div {
  display:inline-block;
  vertical-align:top;
}

.editr--toolbar {
  background: #f6f6f6;
  position: relative;
  display: block;
  border-bottom: 1px solid #e4e4e4;
  width:100%;
}
.editr--toolbar a {
  display: inline-block;
  width: 30px;
  color: #333;
  fill: #333;
  cursor: pointer;
  text-align: center;
  line-height: 1;
}
.editr--toolbar a:hover {
  background: rgba(0,0,0,0.1);
}
.editr--toolbar a:active {
  background: rgba(0,0,0,0.2);
}
.editr--toolbar a svg {
  width: 16px;
  height: 16px;
  margin: 8px auto;
}
.editr--toolbar a svg path {
  fill: inherit;
}
.editr--toolbar a.vw-btn-separator {
  width: 1px;
  padding: 0 8px;
  height:20px;
  position: relative;
}
.editr--toolbar a.vw-btn-separator:hover {
  background: initial;
  cursor: default;
}
.editr--toolbar a.vw-btn-separator i.vw-separator {
  border-left: 1px solid rgba(0,0,0,0.1);
  position: absolute;
  top:7px;
  height:20px;
  width: 1px;
}
.editr--toolbar .dashboard {
  width: 100%;
  position: absolute;
  top: 32px;
  left: 0;
  text-align: left;
  padding: 8px 16px;
  background: rgba(255,255,255,0.95);
  border: 1px solid #f6f6f6;
}
.editr--content {
  min-height: 150px;
  padding: 15px 15px 15px 15px;
  line-height: 1.33;
  font-family: inherit;
  color: inherit;
  overflow-y: auto;
  font-size:14px;
  display:block;
  width:100%;
  background:rgba(0, 0, 0, 0.06) !important;
  -webkit-transition: background 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
    transition: background 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}
.editr--content:hover {
  background:rgba(0, 0, 0, 0.12) !important;
  -webkit-transition: background 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
    transition: background 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}
.editr--content[contenteditable=true]:empty:before {
  content: attr(placeholder);
  color: rgba(0,0,0,0.54);
  display: block; /* For Firefox */
}
.editr--content img {
  max-width: 100%;
}
.editr--content table {
  width: 100%;
  border-collapse: collapse;
}
.editr--content table th {
  text-align: left;
}
.editr--content table th,
.editr--content table td {
  border: 1px solid #ddd;
  padding: 2px;
}
.editr--content:focus {
  outline: 0;
}
.editr--content ul li,
.editr--content ol li {
  list-style-position: inside;
}
@media screen and (max-width: 320px) {
.editr--toolbar a {
    margin: 0 2px;
}
.editr--toolbar a.vw-btn-separator {
    display: none;
}
}/*
 * The MIT License
 * Copyright (c) 2012 Matias Meno <m@tias.me>
 */
@-webkit-keyframes passing-through {
  0% {
    opacity: 0;
    -webkit-transform: translateY(40px);
    -moz-transform: translateY(40px);
    -ms-transform: translateY(40px);
    -o-transform: translateY(40px);
    transform: translateY(40px); }
  30%, 70% {
    opacity: 1;
    -webkit-transform: translateY(0px);
    -moz-transform: translateY(0px);
    -ms-transform: translateY(0px);
    -o-transform: translateY(0px);
    transform: translateY(0px); }
  100% {
    opacity: 0;
    -webkit-transform: translateY(-40px);
    -moz-transform: translateY(-40px);
    -ms-transform: translateY(-40px);
    -o-transform: translateY(-40px);
    transform: translateY(-40px); } }
@-moz-keyframes passing-through {
  0% {
    opacity: 0;
    -webkit-transform: translateY(40px);
    -moz-transform: translateY(40px);
    -ms-transform: translateY(40px);
    -o-transform: translateY(40px);
    transform: translateY(40px); }
  30%, 70% {
    opacity: 1;
    -webkit-transform: translateY(0px);
    -moz-transform: translateY(0px);
    -ms-transform: translateY(0px);
    -o-transform: translateY(0px);
    transform: translateY(0px); }
  100% {
    opacity: 0;
    -webkit-transform: translateY(-40px);
    -moz-transform: translateY(-40px);
    -ms-transform: translateY(-40px);
    -o-transform: translateY(-40px);
    transform: translateY(-40px); } }
@keyframes passing-through {
  0% {
    opacity: 0;
    -webkit-transform: translateY(40px);
    -moz-transform: translateY(40px);
    -ms-transform: translateY(40px);
    -o-transform: translateY(40px);
    transform: translateY(40px); }
  30%, 70% {
    opacity: 1;
    -webkit-transform: translateY(0px);
    -moz-transform: translateY(0px);
    -ms-transform: translateY(0px);
    -o-transform: translateY(0px);
    transform: translateY(0px); }
  100% {
    opacity: 0;
    -webkit-transform: translateY(-40px);
    -moz-transform: translateY(-40px);
    -ms-transform: translateY(-40px);
    -o-transform: translateY(-40px);
    transform: translateY(-40px); } }
@-webkit-keyframes slide-in {
  0% {
    opacity: 0;
    -webkit-transform: translateY(40px);
    -moz-transform: translateY(40px);
    -ms-transform: translateY(40px);
    -o-transform: translateY(40px);
    transform: translateY(40px); }
  30% {
    opacity: 1;
    -webkit-transform: translateY(0px);
    -moz-transform: translateY(0px);
    -ms-transform: translateY(0px);
    -o-transform: translateY(0px);
    transform: translateY(0px); } }
@-moz-keyframes slide-in {
  0% {
    opacity: 0;
    -webkit-transform: translateY(40px);
    -moz-transform: translateY(40px);
    -ms-transform: translateY(40px);
    -o-transform: translateY(40px);
    transform: translateY(40px); }
  30% {
    opacity: 1;
    -webkit-transform: translateY(0px);
    -moz-transform: translateY(0px);
    -ms-transform: translateY(0px);
    -o-transform: translateY(0px);
    transform: translateY(0px); } }
@keyframes slide-in {
  0% {
    opacity: 0;
    -webkit-transform: translateY(40px);
    -moz-transform: translateY(40px);
    -ms-transform: translateY(40px);
    -o-transform: translateY(40px);
    transform: translateY(40px); }
  30% {
    opacity: 1;
    -webkit-transform: translateY(0px);
    -moz-transform: translateY(0px);
    -ms-transform: translateY(0px);
    -o-transform: translateY(0px);
    transform: translateY(0px); } }
@-webkit-keyframes pulse {
  0% {
    -webkit-transform: scale(1);
    -moz-transform: scale(1);
    -ms-transform: scale(1);
    -o-transform: scale(1);
    transform: scale(1); }
  10% {
    -webkit-transform: scale(1.1);
    -moz-transform: scale(1.1);
    -ms-transform: scale(1.1);
    -o-transform: scale(1.1);
    transform: scale(1.1); }
  20% {
    -webkit-transform: scale(1);
    -moz-transform: scale(1);
    -ms-transform: scale(1);
    -o-transform: scale(1);
    transform: scale(1); } }
@-moz-keyframes pulse {
  0% {
    -webkit-transform: scale(1);
    -moz-transform: scale(1);
    -ms-transform: scale(1);
    -o-transform: scale(1);
    transform: scale(1); }
  10% {
    -webkit-transform: scale(1.1);
    -moz-transform: scale(1.1);
    -ms-transform: scale(1.1);
    -o-transform: scale(1.1);
    transform: scale(1.1); }
  20% {
    -webkit-transform: scale(1);
    -moz-transform: scale(1);
    -ms-transform: scale(1);
    -o-transform: scale(1);
    transform: scale(1); } }
@keyframes pulse {
  0% {
    -webkit-transform: scale(1);
    -moz-transform: scale(1);
    -ms-transform: scale(1);
    -o-transform: scale(1);
    transform: scale(1); }
  10% {
    -webkit-transform: scale(1.1);
    -moz-transform: scale(1.1);
    -ms-transform: scale(1.1);
    -o-transform: scale(1.1);
    transform: scale(1.1); }
  20% {
    -webkit-transform: scale(1);
    -moz-transform: scale(1);
    -ms-transform: scale(1);
    -o-transform: scale(1);
    transform: scale(1); } }
.dropzone, .dropzone * {
  box-sizing: border-box; }

.dropzone {
  min-height: 150px;
  border: 2px solid rgba(0, 0, 0, 0.3);
  background: white;
  padding: 20px 20px; }
  .dropzone.dz-clickable {
    cursor: pointer; }
    .dropzone.dz-clickable * {
      cursor: default; }
    .dropzone.dz-clickable .dz-message, .dropzone.dz-clickable .dz-message * {
      cursor: pointer; }
  .dropzone.dz-started .dz-message {
    display: none; }
  .dropzone.dz-drag-hover {
    border-style: solid; }
    .dropzone.dz-drag-hover .dz-message {
      opacity: 0.5; }
  .dropzone .dz-message {
    text-align: center;
    margin: 2em 0; }
  .dropzone .dz-preview {
    position: relative;
    display: inline-block;
    vertical-align: top;
    margin: 16px;
    min-height: 100px; }
    .dropzone .dz-preview:hover {
      z-index: 1000; }
      .dropzone .dz-preview:hover .dz-details {
        opacity: 1; }
    .dropzone .dz-preview.dz-file-preview .dz-image {
      border-radius: 20px;
      background: #999;
      background: linear-gradient(to bottom, #eee, #ddd); }
    .dropzone .dz-preview.dz-file-preview .dz-details {
      opacity: 1; }
    .dropzone .dz-preview.dz-image-preview {
      background: white; }
      .dropzone .dz-preview.dz-image-preview .dz-details {
        -webkit-transition: opacity 0.2s linear;
        -moz-transition: opacity 0.2s linear;
        -ms-transition: opacity 0.2s linear;
        -o-transition: opacity 0.2s linear;
        transition: opacity 0.2s linear; }
    .dropzone .dz-preview .dz-remove {
      font-size: 14px;
      text-align: center;
      display: block;
      cursor: pointer;
      border: none; }
      .dropzone .dz-preview .dz-remove:hover {
        text-decoration: underline; }
    .dropzone .dz-preview:hover .dz-details {
      opacity: 1; }
    .dropzone .dz-preview .dz-details {
      z-index: 20;
      position: absolute;
      top: 0;
      left: 0;
      opacity: 0;
      font-size: 13px;
      min-width: 100%;
      max-width: 100%;
      padding: 2em 1em;
      text-align: center;
      color: rgba(0, 0, 0, 0.9);
      line-height: 150%; }
      .dropzone .dz-preview .dz-details .dz-size {
        margin-bottom: 1em;
        font-size: 16px; }
      .dropzone .dz-preview .dz-details .dz-filename {
        white-space: nowrap; }
        .dropzone .dz-preview .dz-details .dz-filename:hover span {
          border: 1px solid rgba(200, 200, 200, 0.8);
          background-color: rgba(255, 255, 255, 0.8); }
        .dropzone .dz-preview .dz-details .dz-filename:not(:hover) {
          overflow: hidden;
          text-overflow: ellipsis; }
          .dropzone .dz-preview .dz-details .dz-filename:not(:hover) span {
            border: 1px solid transparent; }
      .dropzone .dz-preview .dz-details .dz-filename span, .dropzone .dz-preview .dz-details .dz-size span {
        background-color: rgba(255, 255, 255, 0.4);
        padding: 0 0.4em;
        border-radius: 3px; }
    .dropzone .dz-preview:hover .dz-image img {
      -webkit-transform: scale(1.05, 1.05);
      -moz-transform: scale(1.05, 1.05);
      -ms-transform: scale(1.05, 1.05);
      -o-transform: scale(1.05, 1.05);
      transform: scale(1.05, 1.05);
      -webkit-filter: blur(8px);
      filter: blur(8px); }
    .dropzone .dz-preview .dz-image {
      border-radius: 20px;
      overflow: hidden;
      width: 120px;
      height: 120px;
      position: relative;
      display: block;
      z-index: 10; }
      .dropzone .dz-preview .dz-image img {
        display: block; }
    .dropzone .dz-preview.dz-success .dz-success-mark {
      -webkit-animation: passing-through 3s cubic-bezier(0.77, 0, 0.175, 1);
      -moz-animation: passing-through 3s cubic-bezier(0.77, 0, 0.175, 1);
      -ms-animation: passing-through 3s cubic-bezier(0.77, 0, 0.175, 1);
      -o-animation: passing-through 3s cubic-bezier(0.77, 0, 0.175, 1);
      animation: passing-through 3s cubic-bezier(0.77, 0, 0.175, 1); }
    .dropzone .dz-preview.dz-error .dz-error-mark {
      opacity: 1;
      -webkit-animation: slide-in 3s cubic-bezier(0.77, 0, 0.175, 1);
      -moz-animation: slide-in 3s cubic-bezier(0.77, 0, 0.175, 1);
      -ms-animation: slide-in 3s cubic-bezier(0.77, 0, 0.175, 1);
      -o-animation: slide-in 3s cubic-bezier(0.77, 0, 0.175, 1);
      animation: slide-in 3s cubic-bezier(0.77, 0, 0.175, 1); }
    .dropzone .dz-preview .dz-success-mark, .dropzone .dz-preview .dz-error-mark {
      pointer-events: none;
      opacity: 0;
      z-index: 500;
      position: absolute;
      display: block;
      top: 50%;
      left: 50%;
      margin-left: -27px;
      margin-top: -27px; }
      .dropzone .dz-preview .dz-success-mark svg, .dropzone .dz-preview .dz-error-mark svg {
        display: block;
        width: 54px;
        height: 54px; }
    .dropzone .dz-preview.dz-processing .dz-progress {
      opacity: 1;
      -webkit-transition: all 0.2s linear;
      -moz-transition: all 0.2s linear;
      -ms-transition: all 0.2s linear;
      -o-transition: all 0.2s linear;
      transition: all 0.2s linear; }
    .dropzone .dz-preview.dz-complete .dz-progress {
      opacity: 0;
      -webkit-transition: opacity 0.4s ease-in;
      -moz-transition: opacity 0.4s ease-in;
      -ms-transition: opacity 0.4s ease-in;
      -o-transition: opacity 0.4s ease-in;
      transition: opacity 0.4s ease-in; }
    .dropzone .dz-preview:not(.dz-processing) .dz-progress {
      -webkit-animation: pulse 6s ease infinite;
      -moz-animation: pulse 6s ease infinite;
      -ms-animation: pulse 6s ease infinite;
      -o-animation: pulse 6s ease infinite;
      animation: pulse 6s ease infinite; }
    .dropzone .dz-preview .dz-progress {
      opacity: 1;
      z-index: 1000;
      pointer-events: none;
      position: absolute;
      height: 16px;
      left: 50%;
      top: 50%;
      margin-top: -8px;
      width: 80px;
      margin-left: -40px;
      background: rgba(255, 255, 255, 0.9);
      transform: scale(1);
      -webkit-transform: scale(1);
      border-radius: 8px;
      overflow: hidden; }
      .dropzone .dz-preview .dz-progress .dz-upload {
        background: #333;
        background: linear-gradient(to bottom, #666, #444);
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        width: 0;
        -webkit-transition: width 300ms ease-in-out;
        -moz-transition: width 300ms ease-in-out;
        -ms-transition: width 300ms ease-in-out;
        -o-transition: width 300ms ease-in-out;
        transition: width 300ms ease-in-out; }
    .dropzone .dz-preview.dz-error .dz-error-message {
      display: block; }
    .dropzone .dz-preview.dz-error:hover .dz-error-message {
      opacity: 1;
      pointer-events: auto; }
    .dropzone .dz-preview .dz-error-message {
      pointer-events: none;
      z-index: 1000;
      position: absolute;
      display: block;
      display: none;
      opacity: 0;
      -webkit-transition: opacity 0.3s ease;
      -moz-transition: opacity 0.3s ease;
      -ms-transition: opacity 0.3s ease;
      -o-transition: opacity 0.3s ease;
      transition: opacity 0.3s ease;
      border-radius: 8px;
      font-size: 13px;
      top: 130px;
      left: -10px;
      width: 140px;
      background: #be2626;
      background: linear-gradient(to bottom, #be2626, #a92222);
      padding: 0.5em 1.2em;
      color: white; }
      .dropzone .dz-preview .dz-error-message:after {
        content: '';
        position: absolute;
        top: -6px;
        left: 64px;
        width: 0;
        height: 0;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-bottom: 6px solid #be2626; }

.vue-dropzone {
  border: 2px solid #E5E5E5;
  font-family: 'Arial', sans-serif;
  letter-spacing: 0.2px;
  color: #777;
  transition: background-color 0.2s linear;
}
.vue-dropzone:hover {
  background-color: #F6F6F6;
}
.vue-dropzone i {
  color: #CCC;
}
.vue-dropzone .dz-preview .dz-image {
  border-radius: 0;
  width: 100%;
  height: 100%;
}
.vue-dropzone .dz-preview .dz-image img:not([src]) {
  width: 200px;
  height: 200px;
}
.vue-dropzone .dz-preview .dz-image:hover img {
  transform: none;
  filter: none;
  -webkit-filter: none;
}
.vue-dropzone .dz-preview .dz-details {
  bottom: 0;
  top: 0;
  color: white;
  background-color: rgba(33, 150, 243, 0.8);
  transition: opacity .2s linear;
  text-align: left;
}
.vue-dropzone .dz-preview .dz-details .dz-filename {
  overflow: hidden;
}
.vue-dropzone .dz-preview .dz-details .dz-filename span,
.vue-dropzone .dz-preview .dz-details .dz-size span {
  background-color: transparent;
}
.vue-dropzone .dz-preview .dz-details .dz-filename:not(:hover) span {
  border: none;
}
.vue-dropzone .dz-preview .dz-details .dz-filename:hover span {
  background-color: transparent;
  border: none;
}
.vue-dropzone .dz-preview .dz-progress .dz-upload {
  background: #cccccc;
}
.vue-dropzone .dz-preview .dz-remove {
  position: absolute;
  z-index: 30;
  color: white;
  margin-left: 15px;
  padding: 10px;
  top: inherit;
  bottom: 15px;
  border: 2px white solid;
  text-decoration: none;
  text-transform: uppercase;
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 1.1px;
  opacity: 0;
}
.vue-dropzone .dz-preview:hover .dz-remove {
  opacity: 1;
}
.vue-dropzone .dz-preview .dz-success-mark,
.vue-dropzone .dz-preview .dz-error-mark {
  margin-left: auto;
  margin-top: auto;
  width: 100%;
  top: 35%;
  left: 0;
}
.vue-dropzone .dz-preview .dz-success-mark svg,
.vue-dropzone .dz-preview .dz-error-mark svg {
  margin-left: auto;
  margin-right: auto;
}
.vue-dropzone .dz-preview .dz-error-message {
  top: calc(15%);
  margin-left: auto;
  margin-right: auto;
  left: 0;
  width: 100%;
}
.vue-dropzone .dz-preview .dz-error-message:after {
  bottom: -6px;
  top: initial;
  border-top: 6px solid #a92222;
  border-bottom: none;
}

.form[data-v-ebce4d12] {
  display: flex;
  align-content: flex-end;
}
.form label[data-v-ebce4d12] {
  margin-right: 1rem;
}

.rich-text-container {
  margin-bottom: 20px;
  border-radius: 5px;
}

.source-toggle-toolbar-btn:hover {
  background: rgba(0,0,0,0.1);
}

.source-toggle-toolbar-btn:active {
  background: rgba(0,0,0,0.2);
}

.source-toggle-toolbar-btn .material-icons {
  font-size: 16px;
  line-height: 32px;
  vertical-align: middle;
}

.source-html-editor {
  border: 1px solid #e4e4e4;
  border-radius: 0 0 4px 4px;
}

.source-html-editor textarea {
  font-family: 'Courier New', Courier, monospace;
  font-size: 13px;
}

</style>
